import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { dbOperation, dbTransaction } from '../utils/database';
import { toast } from '../lib/toast';

interface BlueprintContent {
  project_name: string;
  description?: string;
  overview?: string;
  features?: any[];
  requirements?: any[];
  architecture?: any;
  tech_stack?: any;
  [key: string]: any;
}

interface Blueprint {
  id: string;
  project_id: string;
  user_id?: string;
  created_by?: string;
  blueprint_type: 'requirements' | 'technical' | 'design';
  content: BlueprintContent;
  version: number;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Centralized blueprint management service
 * Ensures consistent blueprint creation and updates across the application
 */
export class BlueprintManager {
  /**
   * Get or create blueprint for a project
   * This is the SINGLE entry point for blueprint access
   */
  static async getOrCreateBlueprint(
    projectId: string,
    userId: string,
    projectName?: string,
    projectDescription?: string
  ): Promise<{ blueprint: Blueprint | null; error?: string }> {
    try {
      // First, try to get existing blueprint
      const existingResult = await dbOperation<Blueprint>(
        () => supabase
          .from('project_blueprints')
          .select('*')
          .eq('project_id', projectId)
          .eq('blueprint_type', 'requirements')
          .order('version', { ascending: false })
          .limit(1)
          .maybeSingle(),
        {
          operation: 'get blueprint',
          entity: 'project_blueprints',
          userId,
          projectId,
          showUserError: false
        }
      );

      if (existingResult.success && existingResult.data) {
        logger.debug('Found existing blueprint', { 
          blueprintId: existingResult.data.id,
          projectId 
        });
        return { blueprint: existingResult.data };
      }

      // No existing blueprint, create a new one
      logger.info('Creating new blueprint for project', { projectId, userId });

      // Get project details if not provided
      if (!projectName || !projectDescription) {
        const projectResult = await dbOperation<{ name: string; description?: string }>(
          () => supabase
            .from('projects')
            .select('name, description')
            .eq('id', projectId)
            .single(),
          {
            operation: 'get project',
            entity: 'projects',
            userId,
            projectId,
            showUserError: false
          }
        );

        if (projectResult.success && projectResult.data) {
          projectName = projectName || projectResult.data.name;
          projectDescription = projectDescription || projectResult.data.description;
        }
      }

      // Create new blueprint with default content
      const defaultContent: BlueprintContent = {
        project_name: projectName || 'New Project',
        description: projectDescription || 'Start planning your project here',
        overview: projectDescription || '',
        features: [],
        requirements: [],
        architecture: {
          components: [],
          integrations: [],
          infrastructure: {}
        },
        tech_stack: {
          frontend: [],
          backend: [],
          database: [],
          devops: []
        }
      };

      const createResult = await dbOperation<Blueprint>(
        () => supabase
          .from('project_blueprints')
          .insert({
            project_id: projectId,
            user_id: userId,
            created_by: userId,
            blueprint_type: 'requirements',
            content: defaultContent,
            version: 1,
            is_locked: false
          })
          .select()
          .single(),
        {
          operation: 'create blueprint',
          entity: 'project_blueprints',
          userId,
          projectId
        }
      );

      if (!createResult.success) {
        logger.error('Failed to create blueprint', {
          projectId,
          userId,
          error: createResult.error
        });
        return { 
          blueprint: null, 
          error: createResult.error || 'Failed to create blueprint' 
        };
      }

      // Update project with blueprint_id (if column exists)
      // This is non-critical, so we don't fail if it doesn't work
      await dbOperation(
        () => supabase
          .from('projects')
          .update({ blueprint_id: createResult.data!.id })
          .eq('id', projectId),
        {
          operation: 'update project blueprint_id',
          entity: 'projects',
          userId,
          projectId,
          showUserError: false
        }
      );

      logger.info('Blueprint created successfully', {
        blueprintId: createResult.data?.id,
        projectId
      });

      return { blueprint: createResult.data };
    } catch (error) {
      logger.error('Error in getOrCreateBlueprint', {
        projectId,
        userId,
        error
      });
      return { 
        blueprint: null, 
        error: 'Failed to access blueprint' 
      };
    }
  }

  /**
   * Update blueprint content
   */
  static async updateBlueprint(
    blueprintId: string,
    userId: string,
    updates: Partial<BlueprintContent>,
    incrementVersion: boolean = false
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get current blueprint
      const currentResult = await dbOperation<Blueprint>(
        () => supabase
          .from('project_blueprints')
          .select('*')
          .eq('id', blueprintId)
          .single(),
        {
          operation: 'get current blueprint',
          entity: 'project_blueprints',
          userId,
          showUserError: false
        }
      );

      if (!currentResult.success || !currentResult.data) {
        return { 
          success: false, 
          error: 'Blueprint not found' 
        };
      }

      const currentBlueprint = currentResult.data;

      // Check if blueprint is locked
      if (currentBlueprint.is_locked) {
        toast.error('Blueprint is locked and cannot be modified');
        return { 
          success: false, 
          error: 'Blueprint is locked' 
        };
      }

      // Merge updates with existing content
      const updatedContent = {
        ...currentBlueprint.content,
        ...updates
      };

      if (incrementVersion) {
        // Create new version
        const createResult = await dbOperation<Blueprint>(
          () => supabase
            .from('project_blueprints')
            .insert({
              project_id: currentBlueprint.project_id,
              user_id: userId,
              created_by: userId,
              blueprint_type: currentBlueprint.blueprint_type,
              content: updatedContent,
              version: currentBlueprint.version + 1,
              is_locked: false
            })
            .select()
            .single(),
          {
            operation: 'create blueprint version',
            entity: 'project_blueprints',
            userId,
            projectId: currentBlueprint.project_id
          }
        );

        if (!createResult.success) {
          return { 
            success: false, 
            error: createResult.error || 'Failed to create new version' 
          };
        }

        logger.info('Created new blueprint version', {
          blueprintId: createResult.data?.id,
          version: currentBlueprint.version + 1
        });
      } else {
        // Update existing blueprint
        const updateResult = await dbOperation<Blueprint>(
          () => supabase
            .from('project_blueprints')
            .update({
              content: updatedContent,
              updated_at: new Date().toISOString()
            })
            .eq('id', blueprintId)
            .select()
            .single(),
          {
            operation: 'update blueprint',
            entity: 'project_blueprints',
            userId,
            projectId: currentBlueprint.project_id
          }
        );

        if (!updateResult.success) {
          return { 
            success: false, 
            error: updateResult.error || 'Failed to update blueprint' 
          };
        }

        logger.debug('Updated blueprint content', { blueprintId });
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating blueprint', {
        blueprintId,
        userId,
        error
      });
      return { 
        success: false, 
        error: 'Failed to update blueprint' 
      };
    }
  }

  /**
   * Lock blueprint (make it immutable)
   */
  static async lockBlueprint(
    blueprintId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    const result = await dbOperation<Blueprint>(
      () => supabase
        .from('project_blueprints')
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
          locked_by: userId
        })
        .eq('id', blueprintId)
        .eq('is_locked', false) // Only lock if not already locked
        .select()
        .single(),
      {
        operation: 'lock blueprint',
        entity: 'project_blueprints',
        userId
      }
    );

    if (!result.success) {
      return { 
        success: false, 
        error: result.error || 'Failed to lock blueprint' 
      };
    }

    if (!result.data) {
      return { 
        success: false, 
        error: 'Blueprint is already locked' 
      };
    }

    logger.info('Blueprint locked', { blueprintId, userId });
    toast.success('Blueprint locked successfully');
    
    return { success: true };
  }

  /**
   * Clone blueprint to a new project
   */
  static async cloneBlueprint(
    sourceBlueprintId: string,
    targetProjectId: string,
    userId: string
  ): Promise<{ blueprint: Blueprint | null; error?: string }> {
    try {
      // Get source blueprint
      const sourceResult = await dbOperation<Blueprint>(
        () => supabase
          .from('project_blueprints')
          .select('*')
          .eq('id', sourceBlueprintId)
          .single(),
        {
          operation: 'get source blueprint',
          entity: 'project_blueprints',
          userId,
          showUserError: false
        }
      );

      if (!sourceResult.success || !sourceResult.data) {
        return { 
          blueprint: null, 
          error: 'Source blueprint not found' 
        };
      }

      // Create clone
      const cloneResult = await dbOperation<Blueprint>(
        () => supabase
          .from('project_blueprints')
          .insert({
            project_id: targetProjectId,
            user_id: userId,
            created_by: userId,
            blueprint_type: sourceResult.data!.blueprint_type,
            content: sourceResult.data!.content,
            version: 1,
            is_locked: false,
            cloned_from: sourceBlueprintId
          })
          .select()
          .single(),
        {
          operation: 'clone blueprint',
          entity: 'project_blueprints',
          userId,
          projectId: targetProjectId
        }
      );

      if (!cloneResult.success) {
        return { 
          blueprint: null, 
          error: cloneResult.error || 'Failed to clone blueprint' 
        };
      }

      logger.info('Blueprint cloned successfully', {
        sourceBlueprintId,
        newBlueprintId: cloneResult.data?.id,
        targetProjectId
      });

      return { blueprint: cloneResult.data };
    } catch (error) {
      logger.error('Error cloning blueprint', {
        sourceBlueprintId,
        targetProjectId,
        userId,
        error
      });
      return { 
        blueprint: null, 
        error: 'Failed to clone blueprint' 
      };
    }
  }

  /**
   * Get blueprint history (all versions)
   */
  static async getBlueprintHistory(
    projectId: string,
    userId: string
  ): Promise<{ blueprints: Blueprint[]; error?: string }> {
    const result = await dbOperation<Blueprint[]>(
      () => supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .eq('blueprint_type', 'requirements')
        .order('version', { ascending: false }),
      {
        operation: 'get blueprint history',
        entity: 'project_blueprints',
        userId,
        projectId,
        showUserError: false
      }
    );

    if (!result.success) {
      return { 
        blueprints: [], 
        error: result.error 
      };
    }

    return { blueprints: result.data || [] };
  }
}
